import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Tag } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import RichTextEditor from './RichTextEditor';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Glossário reaproveitado do NoticiaDetalhe
const GLOSSARIO = {
  "jurisprudência": "Conjunto de decisões judiciais repetidas sobre um mesmo assunto, que servem como referência para novos casos.",
  "habeas corpus": "Remédio jurídico que protege a liberdade de ir e vir quando alguém está sofrendo prisão ilegal.",
  "liminar": "Decisão judicial provisória e urgente, tomada antes do julgamento final.",
  "mandado de segurança": "Instrumento para proteger direito líquido e certo contra ato abusivo de autoridade pública.",
  "ADI": "Ação Direta de Inconstitucionalidade — questiona se uma lei fere a Constituição Federal.",
  "STF": "Supremo Tribunal Federal — tribunal máximo do Brasil, responsável por interpretar a Constituição.",
  "STJ": "Superior Tribunal de Justiça — tribunal que uniformiza a aplicação das leis federais no Brasil.",
  "réu": "Pessoa acusada em um processo judicial.",
  "inconstitucional": "Que viola ou contraria a Constituição Federal.",
  "constitucional": "Relativo à Constituição; que está de acordo com a Constituição.",
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const destacarTermosJuridicos = (html) => {
  if (!html) return '';
  let resultado = html;
  const termos = Object.keys(GLOSSARIO).sort((a, b) => b.length - a.length);
  termos.forEach((termo) => {
    const regex = new RegExp(`(?<!<[^>]*)\\b(${termo})\\b(?![^<]*>)`, 'gi');
    resultado = resultado.replace(regex, (match) => {
      const def = GLOSSARIO[match.toLowerCase()] || GLOSSARIO[termo];
      return `<span class="termo-juridico" title="${def}">${match}</span>`;
    });
  });
  return resultado;
};

const PreviewNoticia = ({ form, onCancelar, onPublishSuccess }) => {
  const [isInlineEditing, setIsInlineEditing] = useState(false);

  const [titulo, setTitulo] = useState(form.titulo || '');
  const [subtitulo, setSubtitulo] = useState(form.subtitulo || '');
  const [tagsText, setTagsText] = useState(form.tags || '');
  const [fonte, setFonte] = useState(form.fonte || '');
  const [imagemCapa, setImagemCapa] = useState(form.imagem_capa || '');
  const [categoria, setCategoria] = useState(form.categoria || 'Direito');
  const [videoUrl, setVideoUrl] = useState(form.video_url || '');
  const [conteudoHtml, setConteudoHtml] = useState(form.conteudo_html || '');
  const [publicando, setPublicando] = useState(false);

  const tagsArray = tagsText
    ? tagsText.split(',').map((t) => t.trim()).filter(Boolean)
    : [];
  const embedUrl = getEmbedUrl(videoUrl);
  const conteudoComTermos = destacarTermosJuridicos(conteudoHtml || '');
  const dataHoje = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  // Bloqueia scroll do body enquanto o preview estiver aberto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Fecha ao pressionar Esc (voltar ao rascunho)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancelar]);

  const handlePublish = async () => {
    if (!titulo.trim()) {
      window.alert('O título é obrigatório para publicar.');
      return;
    }
    if (!conteudoHtml || conteudoHtml === '<p><br></p>') {
      window.alert('O texto da notícia é obrigatório para publicar.');
      return;
    }

    setPublicando(true);
    try {
      const tagsArrayFinal = tagsText
        ? tagsText.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      const noticia = {
        titulo: titulo.trim(),
        subtitulo: subtitulo.trim(),
        conteudo_html: conteudoHtml,
        conteudo_completo: '',
        resumo: '',
        tags: tagsArrayFinal.length > 0 ? tagsArrayFinal : [categoria],
        categoria: categoria,
        fonte: fonte.trim() || 'Redação',
        imagem_capa: imagemCapa.trim(),
        video_url: videoUrl.trim(),
        link: '',
        data: serverTimestamp(),
        coletado_em: serverTimestamp(),
        origem: 'cms',
      };

      await addDoc(collection(db, 'noticias'), noticia);

      window.alert('Notícia publicada com sucesso!');
      if (onPublishSuccess) {
        onPublishSuccess();
      }
    } catch (err) {
      console.error(err);
      window.alert('Erro ao salvar no Firebase: ' + err.message);
    } finally {
      setPublicando(false);
    }
  };

  const conteudo = (
    <div className="fixed inset-0 z-[9999] bg-gray-50 overflow-y-auto overflow-x-hidden">
      {/* Faixa de ações principais */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white px-6 py-3 flex flex-wrap items-center justify-between gap-3 shadow-md">
        <span className="text-xs sm:text-sm font-semibold tracking-wide uppercase text-gray-300">
          Pré-visualização de publicação
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handlePublish}
            disabled={publicando}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md transition-colors"
          >
            {publicando ? 'Publicando...' : 'Salvar e Publicar'}
          </button>
          <button
            type="button"
            onClick={() => setIsInlineEditing((prev) => !prev)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-colors ${
              isInlineEditing
                ? 'bg-white text-[#0F172A] border-white'
                : 'bg-transparent text-gray-200 border-gray-500 hover:bg-white/10'
            }`}
          >
            {isInlineEditing ? 'Modo leitura' : 'Continuar editando'}
          </button>
          <button
            type="button"
            onClick={onCancelar}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-gray-200 border border-transparent hover:bg-white/10"
          >
            Cancelar / Voltar ao rascunho
          </button>
        </div>
      </div>

      {/* Header real do site */}
      <Header />

      <main className="bg-gray-50 min-h-screen">
        <article lang="pt-BR" className="max-w-4xl mx-auto px-6 py-12">
          {/* Metadados */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Tag size={18} className="text-[#B49163]" />
              <span className="text-sm font-semibold text-[#B49163]">
                {form.categoria || 'Direito'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar size={16} />
              <span>{dataHoje}</span>
            </div>
            <span className="text-sm text-gray-500">
              {isInlineEditing ? (
                <input
                  type="text"
                  value={fonte}
                  onChange={(e) => setFonte(e.target.value)}
                  placeholder="Fonte da notícia (ex: Conjur, STF, Redação)"
                  className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-[#B49163] text-sm px-1 pb-0.5 text-gray-700 placeholder:text-gray-400"
                />
              ) : (
                <>Fonte: {fonte || 'Redação'}</>
              )}
            </span>
          </div>

          {/* Título */}
          {isInlineEditing ? (
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Digite o título da notícia..."
              className="text-4xl md:text-5xl font-serif font-bold mb-4 leading-tight text-[#0F172A] w-full bg-transparent border-b border-gray-300 focus:outline-none focus:border-[#B49163] placeholder:text-gray-400"
            />
          ) : (
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#0F172A] mb-4 leading-tight">
              {titulo || <span className="text-gray-400 italic">Sem título</span>}
            </h1>
          )}

          {isInlineEditing ? (
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Subtítulo (opcional)
              </label>
              <input
                type="text"
                value={subtitulo}
                onChange={(e) => setSubtitulo(e.target.value)}
                placeholder="Linha de apoio ao título..."
                className="w-full max-w-3xl px-4 py-2.5 border border-gray-300 rounded-lg text-lg text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
              />
            </div>
          ) : subtitulo.trim() ? (
            <p className="text-xl md:text-2xl text-slate-600 leading-snug mb-8 max-w-3xl">
              {subtitulo.trim()}
            </p>
          ) : null}

          {/* Tags clicáveis */}
          {isInlineEditing ? (
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Tags (separadas por vírgula)
              </label>
              <input
                type="text"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="ex: STF, Direito Penal, Habeas Corpus"
                className="w-full max-w-xl px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
              />
            </div>
          ) : tagsArray.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {tagsArray.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-[#0F172A] text-white rounded-full text-sm font-semibold shadow-md"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Imagem de Capa */}
          {isInlineEditing && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                URL da Imagem de Capa
              </label>
              <input
                type="url"
                value={imagemCapa}
                onChange={(e) => setImagemCapa(e.target.value)}
                placeholder="https://..."
                className="w-full max-w-xl px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
              />
            </div>
          )}

          {imagemCapa && (
            <div className="mb-8 flex justify-center">
              <div className="w-full max-w-3xl rounded-lg overflow-hidden shadow-lg">
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <img
                    src={imagemCapa}
                    alt={titulo}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Conteúdo */}
          <div className="noticia-corpo w-full max-w-[100vw] sm:max-w-none text-slate-800 text-lg leading-relaxed text-left [&_p]:mb-5 [&_img]:max-w-full [&_img]:h-auto mb-12">
            {isInlineEditing ? (
              <RichTextEditor
                value={conteudoHtml}
                onChange={setConteudoHtml}
                placeholder="Escreva o conteúdo completo da notícia aqui..."
              />
            ) : conteudoHtml && conteudoHtml !== '<p><br></p>' ? (
              <div dangerouslySetInnerHTML={{ __html: conteudoComTermos }} />
            ) : (
              <p className="text-gray-400 italic">Nenhum conteúdo digitado ainda.</p>
            )}
          </div>

          {/* Player de Vídeo */}
          {embedUrl && (
            <div className="mb-12">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4">Vídeo</h3>
              <div
                className="relative w-full rounded-xl overflow-hidden shadow-lg"
                style={{ paddingTop: '56.25%' }}
              >
                <iframe
                  src={embedUrl}
                  title="Vídeo da notícia"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Rodapé da notícia */}
          <div className="border-t border-gray-300 pt-6 mt-12">
            <p className="text-sm text-gray-400 italic">
              Pré-visualização — esta notícia ainda não foi publicada.
            </p>
          </div>
        </article>
      </main>

      <Footer />

      <style>{`
        .noticia-corpo {
          word-break: normal;
          overflow-wrap: break-word;
        }
        .noticia-corpo p,
        .noticia-corpo li {
          text-indent: 0;
          padding-left: 0;
        }
        .termo-juridico {
          color: #B49163;
          font-weight: 600;
          cursor: help;
          border-bottom: 2px dotted #B49163;
        }
      `}</style>
    </div>
  );

  return createPortal(conteudo, document.body);
};

export default PreviewNoticia;
